import React, { useEffect, useMemo } from 'react';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';
import { expVal } from '@confluence/feature-experiments';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions';
import { isInsightsScheduledDay } from '@atlassian/rovo-growth-pulse/common/utils/insights-schedule';
import { useInsightsStatus } from '@atlassian/rovo-growth-pulse/services/insights-status';

import { useAudioButtonWrapperStore } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import type {
	PageRovoConfigProviderProps,
	RovoConfiguration,
	RovoConfigurationProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import { useInsightsNudgeAction } from '@confluence/page-rovo-config-common/entry-points/useInsightsNudgeAction.ts';
import { useConvoStartersNudgeAction } from '@confluence/page-rovo-config-common/entry-points/useConvoStartersNudgeAction.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useNudgeRecommendationFilter } from './hooks/useNudgeRecommendationFilter.tsx';

const ViewPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	const { contentType, contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	const { cloudId } = useSessionData();

	const viewPageRovoActions = useViewPageRovoActions();
	const insightsNudgeAction = useInsightsNudgeAction();
	const convoStartersNudgeAction = useConvoStartersNudgeAction();
	const enableNudgeCheck = useNudgeRecommendationFilter(contentType, contentId);
	const { setNudges } = useAISmartButtonActions();
	const { eligibleForChangeboarding } = useAudioButtonWrapperStore();

	const assistanceServiceParams = useMemo(
		() => ({
			siteId: cloudId,
			product: 'confluence' as const,
			experienceId: 'ai-mate',
			touchpointSource: 'rovo-insights-nudge',
		}),
		[cloudId],
	);

	const { isInsightsAvailable } = useInsightsStatus({
		enabled: expVal('rovo_growth_chat_pulse', 'isEnabled', false) && isInsightsScheduledDay(),
		assistanceServiceParams,
	});

	const config = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				const actions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);
				return Promise.resolve(actions);
			},
		};
	}, [viewPageRovoActions]);

	useEffect(() => {
		setConfiguration(config);

		if (!enableNudgeCheck) {
			setNudges([]);
			return;
		}
		// TODO: TREX-1199 Refactor ViewPageRovoConfigProvider nudge config into useNudgeConfig hook
		const nudges = [
			viewPageRovoActions.find((a: { actionId: string }) => a.actionId === 'page-catchup')?.action,
			// Only show audio briefing nudge if the user is not eligible for audio briefing changeboarding
			!eligibleForChangeboarding &&
				viewPageRovoActions.find((a: { actionId: string }) => a.actionId === 'audio-briefing')
					?.action,
			// Only register insights nudge when insights are available for this user
			isInsightsAvailable && insightsNudgeAction,
			// Convo starter nudge — label and prompt are enriched from BE at match time.
			convoStartersNudgeAction,
		].filter(Boolean) as ConversationAction[];
		if (nudges.length > 0) {
			setNudges(nudges);
		}
	}, [
		config,
		setConfiguration,
		enableNudgeCheck,
		viewPageRovoActions,
		setNudges,
		eligibleForChangeboarding,
		isInsightsAvailable,
		insightsNudgeAction,
		convoStartersNudgeAction,
	]);

	return null;
};

export const ViewPageRovoConfigProvider = (props: RovoConfigurationProviderProps) => {
	const { contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	return (
		<CommentCountsProvider contentId={contentId}>
			<ViewPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
